import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, errorText } from '../api';
import { useAuth } from '../auth';
import { Badge, Card, Empty, Field, Loading, Notice, SelectField, TextField } from '../components/ui';

interface AbrechnungZeile {
  id: string;
  qr_token: string;
  iso_nummer: string | null;
  owner_name: string | null;
  status: string;
  location_code: string | null;
  vereinnahmt_am: string;
  ausgelagert_am: string | null;
  standtage: number;
  kosten_vereinnahmung: number;
  kosten_auslagerung: number;
  kosten_stand: number;
  kosten_gesamt: number;
  standtage_abgerechnet: number;
  standtage_offen: number;
  offen_vereinnahmung: number;
  offen_auslagerung: number;
  offen_gesamt: number;
}

interface Abrechnung {
  saetze: { vereinnahmung: number; auslagerung: number; standtag: number };
  stichtag: string;
  zeilen: AbrechnungZeile[];
}

interface Lauf {
  id: string;
  bezeichnung: string;
  stichtag: string;
  owner_id: string | null;
  owner_name: string | null;
  summe_euro: number;
  anzahl_container: number;
  erstellt_am: string;
}

interface Position {
  id: string;
  qr_token: string;
  iso_nummer: string | null;
  standtage: number;
  vereinnahmung_abgerechnet: boolean;
  auslagerung_abgerechnet: boolean;
  betrag_euro: number;
}

const euro = (v: number) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v);

const datum = (value: string | null) =>
  value ? new Date(value).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

const heute = () => new Date().toISOString().slice(0, 10);

export function StandtagePage() {
  const { owners, can } = useAuth();
  const navigate = useNavigate();
  const darfAbrechnen = can('admin', 'kaufmann', 'disponent');

  const [daten, setDaten] = useState<Abrechnung | null>(null);
  const [laeufe, setLaeufe] = useState<Lauf[] | null>(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('');
  const [firma, setFirma] = useState('');

  // Abrechnen-Formular
  const [stichtag, setStichtag] = useState(heute());
  const [bezeichnung, setBezeichnung] = useState('');
  const [busy, setBusy] = useState(false);

  // Aufgeklappter Lauf
  const [offenerLauf, setOffenerLauf] = useState<string | null>(null);
  const [positionen, setPositionen] = useState<Position[]>([]);

  const laden = useCallback(async () => {
    setError('');
    try {
      const query: Record<string, string> = {};
      if (status) query.status = status;
      if (firma) query.ownerId = firma;
      if (stichtag) query.stichtag = stichtag;
      const [a, l] = await Promise.all([
        api<Abrechnung>('/api/container/abrechnung', { query }),
        api<Lauf[]>('/api/container/abrechnung/laeufe', { query: firma ? { ownerId: firma } : {} }),
      ]);
      setDaten(a);
      setLaeufe(l);
    } catch (err) {
      setError(errorText(err));
    }
  }, [status, firma, stichtag]);

  useEffect(() => {
    void laden();
  }, [laden]);

  const offenSumme = useMemo(
    () => (daten ? daten.zeilen.reduce((s, z) => s + z.offen_gesamt, 0) : 0),
    [daten],
  );
  const gesamtSumme = useMemo(
    () => (daten ? daten.zeilen.reduce((s, z) => s + z.kosten_gesamt, 0) : 0),
    [daten],
  );

  async function abrechnen() {
    if (offenSumme <= 0) {
      setError('Für die aktuelle Auswahl ist nichts offen.');
      return;
    }
    const firmaName = firma ? owners.find((o) => o.id === firma)?.name ?? '' : 'alle Firmen';
    const anzahl = daten?.zeilen.filter((z) => z.offen_gesamt > 0).length ?? 0;
    if (
      !window.confirm(
        `Abrechnung bis ${datum(stichtag)} für ${firmaName} anlegen?\n\n` +
          `${anzahl} Container, offener Betrag ${euro(offenSumme)}.\n\n` +
          'Danach gilt dieser Teil als abgerechnet und erscheint nicht mehr im offenen Rest.',
      )
    ) {
      return;
    }
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const lauf = await api<Lauf>('/api/container/abrechnung/laeufe', {
        method: 'POST',
        body: {
          stichtag,
          ownerId: firma || null,
          bezeichnung: bezeichnung.trim() || null,
        },
      });
      setMessage(`Abrechnung „${lauf.bezeichnung}" angelegt: ${euro(lauf.summe_euro)} für ${lauf.anzahl_container} Container.`);
      setBezeichnung('');
      await laden();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function laufLoeschen(l: Lauf) {
    if (!window.confirm(`Abrechnungslauf „${l.bezeichnung}" (${euro(l.summe_euro)}) löschen?\n\nDie darin abgerechneten Beträge werden danach wieder als offen gezählt.`)) {
      return;
    }
    setError('');
    setMessage('');
    try {
      await api(`/api/container/abrechnung/laeufe/${l.id}`, { method: 'DELETE' });
      setMessage(`Lauf „${l.bezeichnung}" gelöscht.`);
      if (offenerLauf === l.id) setOffenerLauf(null);
      await laden();
    } catch (err) {
      setError(errorText(err));
    }
  }

  async function laufOeffnen(id: string) {
    if (offenerLauf === id) {
      setOffenerLauf(null);
      return;
    }
    try {
      const l = await api<Lauf & { positionen: Position[] }>(`/api/container/abrechnung/laeufe/${id}`);
      setPositionen(l.positionen);
      setOffenerLauf(id);
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}
      {message && <Notice kind="success">{message}</Notice>}

      <Card>
        <h3 style={{ marginTop: 0 }}>Standtage &amp; Lagergeld (Container)</h3>
        <p className="small" style={{ marginTop: 0 }}>
          {daten
            ? `Vereinnahmung ${euro(daten.saetze.vereinnahmung)} · Auslagerung ${euro(
                daten.saetze.auslagerung,
              )} · je Standtag ${euro(daten.saetze.standtag)}`
            : ' '}
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          <SelectField label="Firma" value={firma} onChange={setFirma} options={owners} render={(o) => o.name} />
          <div>
            <Field label="Status">
              <div className="toolbar">
                <button className={status === '' ? 'primary' : ''} onClick={() => setStatus('')}>Alle</button>
                <button className={status === 'eingelagert' ? 'primary' : ''} onClick={() => setStatus('eingelagert')}>Eingelagert</button>
                <button className={status === 'ausgelagert' ? 'primary' : ''} onClick={() => setStatus('ausgelagert')}>Ausgelagert</button>
              </div>
            </Field>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 8 }}>
          <div><div className="small">Aufgelaufen gesamt</div><div style={{ fontSize: 20, fontWeight: 700 }}>{euro(gesamtSumme)}</div></div>
          <div><div className="small">Offen (noch nicht abgerechnet)</div><div style={{ fontSize: 20, fontWeight: 800, color: offenSumme > 0 ? '#b45309' : '#15803d' }}>{euro(offenSumme)}</div></div>
        </div>
      </Card>

      {darfAbrechnen && (
        <Card>
          <h3 style={{ marginTop: 0 }}>Abrechnung abschließen</h3>
          <p className="small" style={{ marginTop: 0 }}>
            Schreibt den offenen Betrag bis zum Stichtag fest (für die oben gewählte Firma). Danach läuft die Zählung ab dem Stichtag weiter.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
            <TextField label="Stichtag (abgerechnet bis einschließlich)" type="date" value={stichtag} onChange={setStichtag} />
            <TextField label="Bezeichnung (optional)" value={bezeichnung} onChange={setBezeichnung} placeholder={`z. B. ${new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}`} />
          </div>
          <button className="primary" disabled={busy || offenSumme <= 0} onClick={() => void abrechnen()}>
            {busy ? 'Wird angelegt …' : `Jetzt abrechnen (${euro(offenSumme)})`}
          </button>
        </Card>
      )}

      <Card padded={false}>
        {!daten ? (
          <Loading />
        ) : daten.zeilen.length === 0 ? (
          <Empty>Keine Container in der Auswahl.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Container-Nr</th>
                  <th>Firma</th>
                  <th>Status</th>
                  <th>Lagerplatz</th>
                  <th>Vereinnahmt</th>
                  <th className="num">Standtage</th>
                  <th className="num">abger.</th>
                  <th className="num">offen</th>
                  <th className="num">Aufgel. gesamt</th>
                  <th className="num">Offen €</th>
                </tr>
              </thead>
              <tbody>
                {daten.zeilen.map((z) => (
                  <tr key={z.id}>
                    <td className="mono">
                      <button
                        className="linklike"
                        title="Container öffnen"
                        onClick={() => navigate(`/container?code=${encodeURIComponent(z.qr_token)}`)}
                      >
                        {z.qr_token}
                      </button>
                    </td>
                    <td className="mono small">{z.iso_nummer ?? '—'}</td>
                    <td className="small">{z.owner_name ?? '—'}</td>
                    <td><Badge kind={z.status === 'eingelagert' ? 'ok' : 'default'}>{z.status}</Badge></td>
                    <td className="mono small">{z.location_code ?? '—'}</td>
                    <td className="small">{datum(z.vereinnahmt_am)}</td>
                    <td className="num">{z.standtage}</td>
                    <td className="num small">{z.standtage_abgerechnet || '—'}</td>
                    <td className="num">{z.standtage_offen || '—'}</td>
                    <td className="num small">{euro(z.kosten_gesamt)}</td>
                    <td className="num" style={{ fontWeight: 700, color: z.offen_gesamt > 0 ? '#b45309' : '#15803d' }}>{euro(z.offen_gesamt)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={9} style={{ textAlign: 'right', fontWeight: 700 }}>Summe aufgelaufen / offen</td>
                  <td className="num" style={{ fontWeight: 700 }}>{euro(gesamtSumme)}</td>
                  <td className="num" style={{ fontWeight: 800 }}>{euro(offenSumme)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>

      <Card padded={false}>
        <div style={{ padding: '12px 16px 0' }}>
          <h3 style={{ margin: 0 }}>Abrechnungsläufe</h3>
        </div>
        {!laeufe ? (
          <Loading />
        ) : laeufe.length === 0 ? (
          <Empty>Noch keine Abrechnung durchgeführt.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Bezeichnung</th>
                  <th>Stichtag</th>
                  <th>Firma</th>
                  <th className="num">Container</th>
                  <th className="num">Summe</th>
                  <th>Erstellt</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {laeufe.map((l) => (
                  <Fragment key={l.id}>
                    <tr onClick={() => void laufOeffnen(l.id)} style={{ cursor: 'pointer' }}>
                      <td>{l.bezeichnung}</td>
                      <td className="small">{datum(l.stichtag)}</td>
                      <td className="small">{l.owner_name ?? 'alle'}</td>
                      <td className="num">{l.anzahl_container}</td>
                      <td className="num" style={{ fontWeight: 700 }}>{euro(l.summe_euro)}</td>
                      <td className="small">{datum(l.erstellt_am)}</td>
                      <td className="small" style={{ whiteSpace: 'nowrap' }}>
                        {offenerLauf === l.id ? '▲' : '▼'}
                        {darfAbrechnen && (
                          <button
                            className="small"
                            style={{ marginLeft: 8 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              void laufLoeschen(l);
                            }}
                            title="Abrechnungslauf löschen"
                          >
                            Löschen
                          </button>
                        )}
                      </td>
                    </tr>
                    {offenerLauf === l.id && (
                      <tr>
                        <td colSpan={7} style={{ background: '#f7f9fc' }}>
                          {positionen.length === 0 ? (
                            <Empty>Keine Positionen.</Empty>
                          ) : (
                            <table>
                              <thead>
                                <tr>
                                  <th>Code</th>
                                  <th>Container-Nr</th>
                                  <th className="num">Standtage</th>
                                  <th>Vereinn.</th>
                                  <th>Ausl.</th>
                                  <th className="num">Betrag</th>
                                </tr>
                              </thead>
                              <tbody>
                                {positionen.map((p) => (
                                  <tr key={p.id}>
                                    <td className="mono">{p.qr_token}</td>
                                    <td className="mono small">{p.iso_nummer ?? '—'}</td>
                                    <td className="num">{p.standtage || '—'}</td>
                                    <td>{p.vereinnahmung_abgerechnet ? 'ja' : '—'}</td>
                                    <td>{p.auslagerung_abgerechnet ? 'ja' : '—'}</td>
                                    <td className="num" style={{ fontWeight: 700 }}>{euro(p.betrag_euro)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
